/**
 * HarmonicStorytelling
 * V11 Feature 4: Harmonic Story Arcs
 * 
 * Injects professional harmonic "tricks" that create narrative
 * surprise and satisfaction—the moments that make songs memorable.
 * 
 * - Deceptive Cadence: V→vi surprise instead of V→I
 * - Pattern Breaking: Pivot to new key after 3x repeat
 * - The Lift: Half-step key change in extended choruses
 * - Tritone Substitution: ♭II7 for V7 on dramatic moments
 */

// Feature 4: Harmonic Story Arcs & Genre-Aware Modulations
export interface StorytellingDecision {
    useDeceptiveCadence: boolean;
    deceptiveTarget: number | null;
    shouldPivot: boolean;
    pivotSemitones: number;
    // New V12.5 Modulation Strategy
    modulation: {
        shouldModulate: boolean;
        semitones: number;
        type: 'TruckDriver' | 'CircleOfFifths' | 'RelativeMinor' | 'ChromaticMediant' | 'None';
    };
    tritoneSubstitution: {
        shouldUse: boolean;
        rootOffset: number;
    };
}

export class HarmonicStorytelling {
    // Pattern tracking
    private recentPatterns: string[] = [];
    private readonly PATTERN_MEMORY = 4;
    private readonly REPEAT_THRESHOLD = 3;

    // Modulation State
    private sectionTouchCount: number = 0;
    private hasModulatedThisSession: boolean = false;
    private sectionCounter: Record<string, number> = { Verse: 0, Chorus: 0, Bridge: 0 };
    private readonly TRUCK_DRIVER_THRESHOLD = 24; // Touches before modulation

    // Cadence tracking
    private lastDegree: number = 1;
    private lastQuality: string = 'major';

    // Dramatic moment detection
    private intensityHistory: number[] = [];
    private readonly INTENSITY_WINDOW = 8;

    constructor() {
        this.reset();
    }

    reset(): void {
        this.recentPatterns = [];
        this.sectionTouchCount = 0;
        this.hasModulatedThisSession = false;
        this.sectionCounter = { Verse: 0, Chorus: 0, Bridge: 0 };
        this.lastDegree = 1;
        this.lastQuality = 'major';
        this.intensityHistory = [];
    }

    /**
     * Analyze context and return storytelling decisions.
     */
    getDecision(
        currentDegree: number,
        currentQuality: string,
        targetDegree: number,
        currentSection: 'Verse' | 'Chorus' | 'Bridge',
        intensity: number,
        progressionComplexity: 'Simple' | 'Medium' | 'Complex' | 'Exotic' // V12.5 Requirement
    ): StorytellingDecision {
        // Track intensity
        this.intensityHistory.push(intensity);
        if (this.intensityHistory.length > this.INTENSITY_WINDOW) {
            this.intensityHistory.shift();
        }

        // Track touches in current section
        this.sectionTouchCount++;

        // Track pattern
        const patternKey = `${currentDegree}-${currentQuality}`;
        this.recentPatterns.push(patternKey);
        if (this.recentPatterns.length > this.PATTERN_MEMORY) {
            this.recentPatterns.shift();
        }

        this.lastDegree = currentDegree;
        this.lastQuality = currentQuality;

        return {
            ...this.checkDeceptiveCadence(currentDegree, targetDegree),
            ...this.checkPatternBreak(),
            modulation: this.checkKeyModulation(currentSection, progressionComplexity, intensity),
            ...this.checkTritoneSubstitution(currentDegree, intensity)
        };
    }

    /**
     * V12.5: Genre-Aware Intelligent Key Modulation
     */
    private checkKeyModulation(
        section: 'Verse' | 'Chorus' | 'Bridge',
        complexity: 'Simple' | 'Medium' | 'Complex' | 'Exotic',
        intensity: number
    ): { shouldModulate: boolean; semitones: number; type: 'TruckDriver' | 'CircleOfFifths' | 'RelativeMinor' | 'ChromaticMediant' | 'None' } {

        // ONE MODULATION PER SESSION LIMIT (Keep it special)
        if (this.hasModulatedThisSession) {
            return { shouldModulate: false, semitones: 0, type: 'None' };
        }

        // 1. POP ANTHEM ("Truck Driver" Gear Shift)
        // Happens in late Choruses (2nd or 3rd) after building tension
        if (complexity === 'Simple' && section === 'Chorus') {
            const isLateChorus = this.sectionCounter['Chorus'] >= 2;
            const isDeepInChorus = this.sectionTouchCount > this.TRUCK_DRIVER_THRESHOLD;

            if (isLateChorus && isDeepInChorus && intensity > 0.6) {
                // 15% chance per touch to trigger once conditions met
                if (Math.random() < 0.15) {
                    this.hasModulatedThisSession = true;
                    if (process.env.NODE_ENV === 'development') console.log('[Storytelling] Pop Modulation: Truck Driver (+2)');
                    return { shouldModulate: true, semitones: 2, type: 'TruckDriver' }; // Whole step up
                }
            }
        }

        // 2. JAZZ / COMPLEX ("Bridge Pivot")
        // Happens entering or during the Bridge
        if ((complexity === 'Medium' || complexity === 'Complex') && section === 'Bridge') {
            // High chance to pivot in Bridge
            if (this.sectionTouchCount > 8 && Math.random() < 0.1) {
                this.hasModulatedThisSession = true;
                // Circle of Fifths or Relative Minor
                const strategy = Math.random() < 0.5 ? 'CircleOfFifths' : 'RelativeMinor';
                const semitones = strategy === 'CircleOfFifths' ? 7 : 3;

                if (process.env.NODE_ENV === 'development') console.log(`[Storytelling] Jazz Modulation: ${strategy} (+${semitones})`);
                return { shouldModulate: true, semitones, type: strategy as any };
            }
        }

        // 3. CINEMATIC / EXOTIC ("Dramatic Shift")
        // Happens on high intensity spikes anywhere
        if (complexity === 'Exotic') {
            // Detect sudden intensity spike
            const avg = this.getAverageIntensity();
            if (intensity > 0.8 && intensity > avg * 1.5) {
                if (Math.random() < 0.2) {
                    this.hasModulatedThisSession = true;
                    // Chromatic Mediant (Major 3rd)
                    if (process.env.NODE_ENV === 'development') console.log('[Storytelling] Cinematic Modulation: Chromatic Mediant (+4)');
                    return { shouldModulate: true, semitones: 4, type: 'ChromaticMediant' };
                }
            }
        }

        return { shouldModulate: false, semitones: 0, type: 'None' };
    }

    private checkDeceptiveCadence(
        currentDegree: number,
        targetDegree: number
    ): { useDeceptiveCadence: boolean; deceptiveTarget: number | null } {
        const isVtoI = currentDegree === 5 && targetDegree === 1;
        if (isVtoI && Math.random() < 0.15) {
            return { useDeceptiveCadence: true, deceptiveTarget: 6 };
        }
        return { useDeceptiveCadence: false, deceptiveTarget: null };
    }

    private checkPatternBreak(): { shouldPivot: boolean; pivotSemitones: number } {
        if (this.recentPatterns.length < this.REPEAT_THRESHOLD) return { shouldPivot: false, pivotSemitones: 0 };
        const lastPattern = this.recentPatterns[this.recentPatterns.length - 1];
        const repeatCount = this.recentPatterns.filter(p => p === lastPattern).length;

        if (repeatCount >= this.REPEAT_THRESHOLD) {
            this.recentPatterns = [];
            return { shouldPivot: true, pivotSemitones: 7 }; // V12.5: Simplify to Dominant pivot
        }
        return { shouldPivot: false, pivotSemitones: 0 };
    }

    private getAverageIntensity(): number {
        if (this.intensityHistory.length === 0) return 0.5;
        return this.intensityHistory.reduce((a, b) => a + b, 0) / this.intensityHistory.length;
    }

    private checkTritoneSubstitution(currentDegree: number, intensity: number): { tritoneSubstitution: { shouldUse: boolean; rootOffset: number } } {
        if (currentDegree !== 5) return { tritoneSubstitution: { shouldUse: false, rootOffset: 0 } };
        const avg = this.getAverageIntensity();
        if (intensity > avg * 1.2 && intensity > 0.6 && Math.random() < 0.2) {
            return { tritoneSubstitution: { shouldUse: true, rootOffset: -6 } };
        }
        return { tritoneSubstitution: { shouldUse: false, rootOffset: 0 } };
    }

    onSectionChange(newSection: 'Verse' | 'Chorus' | 'Bridge'): void {
        this.sectionTouchCount = 0;
        this.sectionCounter[newSection]++; // Increment count for this section type
        if (process.env.NODE_ENV === 'development') console.log(`[Structure] Entered ${newSection} (${this.sectionCounter[newSection]})`);
    }

    applyDeceptiveCadence(originalDegree: number, deceptiveTarget: number, scaleNotes: number[]): number {
        const targetIndex = (deceptiveTarget - 1) % scaleNotes.length;
        return scaleNotes[targetIndex];
    }
}
